import { useQueryClient } from '@tanstack/react-query';
import { BellOff, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { Paged } from '@kts/shared';
import { formatDateTime } from '@kts/shared';
import { useT } from '@kts/i18n';
import { Button, Spinner } from '@kts/ui';
import { api } from '@/lib/api';
import { useGet } from '@/lib/hooks';

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  tone: 'info' | 'success' | 'warning' | 'danger';
  createdAt: string;
  readAt: string | null;
}

const toneColor: Record<Notification['tone'], string> = {
  info: 'var(--info)',
  success: 'var(--good)',
  warning: 'var(--warning)',
  danger: 'var(--critical)',
};

export function NotificationsMenu({ onNavigate }: { onNavigate: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const t = useT('app');
  const list = useGet<Paged<Notification>>('/notifications', { pageSize: 8 });
  const markAll = async () => {
    await api.post('/notifications/read', { all: true });
    await qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/notifications') });
  };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', padding: '6px 6px 10px 10px' }}>
        <div style={{ fontWeight: 700, flex: 1 }}>{t('notifications.title')}</div>
        <Button size="sm" variant="ghost" icon={<CheckCheck />} onClick={markAll}>
          {t('notifications.markAll')}
        </Button>
      </div>
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {list.isLoading && (
          <div style={{ display: 'grid', placeItems: 'center', padding: 24 }}>
            <Spinner />
          </div>
        )}
        {list.data?.items.length === 0 && (
          <div style={{ display: 'grid', placeItems: 'center', gap: 8, padding: 28, color: 'var(--muted)' }}>
            <BellOff size={22} />
            {t('notifications.empty')}
          </div>
        )}
        {list.data?.items.map((n) => (
          <button
            key={n.id}
            type="button"
            className="kts-menu__item"
            style={{ alignItems: 'flex-start', opacity: n.readAt ? 0.65 : 1 }}
            onClick={async () => {
              await api.post('/notifications/read', { ids: [n.id] }).catch(() => undefined);
              void qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/notifications') });
              onNavigate();
              if (n.link) navigate(n.link);
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: 4, background: toneColor[n.tone], marginTop: 6, flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 600 }}>{n.title}</span>
              {n.body && <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-2)', marginTop: 2 }}>{n.body}</span>}
              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>{formatDateTime(n.createdAt)}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
